import React, { useContext, useMemo } from 'react';

import { useSessionStorage } from 'usehooks-ts';

type MediaDeviceContextValue = {
  selectedAudioDeviceId?: string;
  setSelectedAudioDeviceId: (deviceId: string) => void;
  selectedVideoDeviceId?: string;
  setSelectedVideoDeviceId: (deviceId: string) => void;
};

export const MediaDeviceContext =
  React.createContext<MediaDeviceContextValue | null>(null);

export const useMediaDeviceContext = () => {
  const ctx = useContext(MediaDeviceContext);
  if (ctx === null) {
    throw new Error('Missing parent <MediaDeviceProvider> component');
  }
  return ctx;
};

export const MediaDeviceProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useSessionStorage<
    string | undefined
  >('audio-device-id', undefined);

  const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useSessionStorage<
    string | undefined
  >('video-device-id', undefined);

  const providerValue = useMemo(
    () =>
      ({
        selectedAudioDeviceId,
        setSelectedAudioDeviceId,
        selectedVideoDeviceId,
        setSelectedVideoDeviceId,
      }) satisfies MediaDeviceContextValue,
    [
      selectedAudioDeviceId,
      selectedVideoDeviceId,
      setSelectedAudioDeviceId,
      setSelectedVideoDeviceId,
    ]
  );

  return (
    <MediaDeviceContext.Provider value={providerValue}>
      {children}
    </MediaDeviceContext.Provider>
  );
};
